<template>
    <div>
        <div class="flex mb-4 is-justify-content-flex-end gap-1">
            <v-button type="is-primary" @click="groupModalActive = true" icon="add">
                {{ $t('add_group') }}
            </v-button>
            <v-button @click="exportKpiDaily" color="green" :disabled="isLoading">
                <span v-if="!isLoading">{{ $t('export') }}</span>
                <span v-else>
                    <i class="fa fa-spinner fa-spin"></i> {{ $t('loading') }}...
                </span>
            </v-button>
        </div>

        <div class="panel">
            <b-table :data="kpiGroups" :loading="loading" paginated :per-page="25" :pagination-simple="true"
                :total="kpiGroups.length" detailed detail-key="id" icon-pack="fas" class="text-xs border-blue-lighter "
                bordered narrowed striped>
                <template slot="empty">
                    <div v-if="!loading" class="m-4 text-lg text-dark-grey">
                        <span>{{ $t('no_results') }}</span>
                    </div>
                </template>

                <!-- columnas principales -->
                <b-table-column field="name" :label="$t('name')" width="40%" class="text-left" v-slot="group">
                    <span>{{ group.row.name }}</span>
                </b-table-column>
                <b-table-column field="description" :label="$t('description')" width="40%" class="text-left"
                    v-slot="group">
                    <span>{{ group.row.description }}</span>
                </b-table-column>
                <b-table-column :label="$t('actions')" width="180" centered v-slot="group">
                    <div class="flex items-center justify-center space-x-2">
                        <span class="mr-2">{{ group.row.children ? group.row.children.length : 0 }}</span>
                        <b-tooltip :label="$t('add')" position="is-left">
                            <div @click.stop="openSubgroupModal(group.row)" class="cursor-pointer flex text-green ml-2">
                                <b-icon pack="fas" icon="plus-circle" />
                            </div>
                        </b-tooltip>
                        <b-tooltip :label="$t('edit')" position="is-left">
                            <div @click.stop="editGroup(group.row)" class="cursor-pointer flex text-blue ml-2">
                                <b-icon pack="fas" icon="pencil-alt" />
                            </div>
                        </b-tooltip>
                        <b-tooltip :label="$t('remove')" position="is-left" type="is-danger">
                            <div @click.stop="deleteGroup(group.row)" class="cursor-pointer flex text-red ml-2">
                                <b-icon pack="fas" icon="trash-alt" />
                            </div>
                        </b-tooltip>
                    </div>
                </b-table-column>

                <!-- Subgrupos (children) -->
                <template slot="detail" slot-scope="group">
                    <b-collapse class="card" animation="slide" aria-id="contentIdForA11y3">
                        <template #trigger="props">
                            <div class="card-header" role="button" aria-controls="contentIdForA11y3"
                                :aria-expanded="props.open">
                                <div class="card-header-title">
                                    <h4>
                                        <b>{{ $t('subgroups') }}</b>
                                    </h4>
                                </div>
                                <a class="card-header-icon">
                                    <b-icon icon-pack="fas" :icon="props.open ? 'caret-down' : 'caret-up'"></b-icon>
                                </a>
                            </div>
                        </template>
                        <div class="card-content p-0">
                            <div class="content">
                                <b-table :data="group.row.children" :loading="loading" icon-pack="fas"
                                    class="text-xs border-blue-lighter" bordered narrowed striped detailed
                                    detail-key="id">
                                    <template slot="empty">
                                        <div class="m-4 text-xs text-dark-grey">
                                            <span>{{ $t('no_subgroups') }}</span>
                                        </div>
                                    </template>
                                    <!-- columnas de subgrupos -->
                                    <b-table-column field="name" :label="$t('name')" width="20%" class="text-left"
                                        v-slot="child">
                                        <span>{{ child.row.name }}</span>
                                    </b-table-column>
                                    <b-table-column field="description" :label="$t('description')" width="30%"
                                        class="text-left" v-slot="child">
                                        <span>{{ child.row.description }}</span>
                                    </b-table-column>
                                    <b-table-column field="icon" :label="$t('icon')" width="10%" class="text-center"
                                        v-slot="child">
                                        <img v-if="child.row.icon" :src="asset('public/kpigroups/' + child.row.icon)"
                                            :alt="child.row.icon" style="height: 24px;" />
                                    </b-table-column>
                                    <b-table-column :label="$t('actions')" width="20%" centered v-slot="child">
                                        <div class="flex items-center justify-center space-x-2">
                                            <span class="mr-2">{{ child.row.activity_objects ?
                                                child.row.activity_objects.length : 0
                                                }}</span>
                                            <b-tooltip :label="$t('add')" position="is-left">
                                                <div @click.stop="openActivitiesModal(child.row)"
                                                    class="cursor-pointer flex text-green ml-2">
                                                    <b-icon pack="fas" icon="plus-circle" />
                                                </div>
                                            </b-tooltip>
                                            <b-tooltip :label="$t('edit')" position="is-left">
                                                <div @click.stop="editSubgroup(child.row, group.row)"
                                                    class="cursor-pointer flex text-blue ml-2">
                                                    <b-icon pack="fas" icon="pencil-alt" />
                                                </div>
                                            </b-tooltip>
                                            <b-tooltip :label="$t('remove')" position="is-left" type="is-danger">
                                                <div @click.stop="deleteGroup(child.row)"
                                                    class="cursor-pointer flex text-red ml-2">
                                                    <b-icon pack="fas" icon="trash-alt" />
                                                </div>
                                            </b-tooltip>
                                        </div>
                                    </b-table-column>
                                    <!-- Actividades del subgrupo (detalle desplegable) -->
                                    <template slot="detail" slot-scope="child">
                                        <b-collapse class="card" animation="slide" aria-id="contentIdForA11y4">
                                            <template #trigger="props">
                                                <div class="card-header" role="button" aria-controls="contentIdForA11y4"
                                                    :aria-expanded="props.open">
                                                    <div class="card-header-title">
                                                        <h4>
                                                            <b>{{ $t('assignments') }}</b>
                                                        </h4>
                                                    </div>
                                                    <a class="card-header-icon">
                                                        <b-icon icon-pack="fas"
                                                            :icon="props.open ? 'caret-down' : 'caret-up'"></b-icon>
                                                    </a>
                                                </div>
                                            </template>
                                            <div class="card-content p-0">
                                                <div class="content">
                                                    <b-table :data="child.row.assignment_objects" :loading="loading"
                                                        icon-pack="fas" class="text-xs border-blue-lighter" bordered
                                                        narrowed striped>
                                                        <template slot="empty">
                                                            <div class="m-4 text-xs text-dark-grey">
                                                                <span>{{ $t('empty_activities_table_msg') }}</span>
                                                            </div>
                                                        </template>
                                                        <!-- columnas de actividades -->
                                                        <b-table-column field="name" :label="$t('code')" width="50%"
                                                            class="text-left" v-slot="assignment">
                                                            <span>{{ assignment.row.code }}</span>
                                                        </b-table-column>
                                                        <b-table-column field="measurements" :label="$t('name')"
                                                            width="20%" class="text-left" v-slot="assignment">
                                                            <span>{{ assignment.row.name }}</span>
                                                        </b-table-column>
                                                        <b-table-column :label="$t('actions')" width="20%"
                                                            class="text-center" v-slot="activity">
                                                            <b-tooltip :label="$t('remove')" position="is-left"
                                                                type="is-danger">
                                                                <div @click.stop="confirmDeleteAssignment(child.row, activity.row)"
                                                                    class="cursor-pointer flex text-red ml-2">
                                                                    <b-icon pack="fas" icon="trash-alt" />
                                                                </div>
                                                            </b-tooltip>
                                                        </b-table-column>
                                                    </b-table>
                                                </div>
                                            </div>
                                        </b-collapse>
                                    </template>
                                </b-table>
                            </div>
                        </div>
                    </b-collapse>
                </template>
            </b-table>
            <div class="flex items-end">
                <svg width="60" height="60" viewBox="0 0 60 65" xmlns="http://www.w3.org/2000/svg">
                    <!-- L-shaped arrow as single line -->
                    <path d="M10 10 L10 50 L60 50 L50 40 M60 50 L50 60" 
                        fill="none" 
                        stroke="#4a5568" 
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"/>
                </svg>

                {{ $t('show_kpi_of_selected_groups') }}
                <v-button type="is-primary" @click="onViewKpi()" class="ml-4 mr-4">
                    {{ $t('see_kpi') }}
                </v-button>
            </div>
        </div>

        <!-- Modal: Crear Grupo Principal -->
        <b-modal :active.sync="groupModalActive" :width="600" scroll="keep" :can-cancel="canCancel"
            @close="closeGroupModal">
            <div class="border border-blue-light rounded shadow bg-white p-4">
                <div class="modal-card-body_ relative w-full">
                    <header class="font-thin uppercase text-blue-light modal-card-head_ mb-4">
                        {{ $t('add_group') }}
                    </header>
                    <section class="px-2 mb-3">
                        <b-field :label="$t('name')">
                            <b-input v-model="group.name" :placeholder="$t('name')" required />
                        </b-field>
                        <b-field :label="$t('description')">
                            <b-input v-model="group.description" :placeholder="$t('description')" />
                        </b-field>
                    </section>
                </div>
                <footer class="modal-card-foot_ border-grey-light border-t border-dashed mb-2">
                    <div class="w-full inline-flex justify-end mt-4">
                        <b-button type="is-primary" @click="saveGroup" :disabled="!group.name">
                            {{ $t('add_generic') }}
                        </b-button>
                        <b-button @click="groupModalActive = false" class="ml-2">{{ $t('cancel') }}</b-button>
                    </div>
                </footer>
            </div>
        </b-modal>


        <!-- Modal: Crear SubGrupo -->
        <b-modal :active.sync="subgroupModalActive" :width="600" :can-cancel="canCancel"
            @close="subgroupModalActive = false">
            <div class="border border-blue-light rounded shadow bg-white p-4">
                <div class="modal-card-body_ w-full">
                    <header class="font-thin uppercase text-blue-light modal-card-head_ mb-4">
                        {{ $t('add_group') }}
                        <span v-if="selectedGroup" class="has-text-grey is-size-7">
                            ({{ $t('group') }}: {{ selectedGroup.name }})
                        </span>
                    </header>
                    <section class="px-2 mb-3">
                        <b-field :label="$t('name')">
                            <b-input v-model="subgroup.name" :placeholder="$t('name')" required />
                        </b-field>
                        <b-field :label="$t('description')">
                            <b-input v-model="subgroup.description" :placeholder="$t('description')" />
                        </b-field>
                        <b-field label="Icono" class="w-full">
                            <div class="relative w-full">
                                <button class="button is-fullwidth" type="button" @click="toggleDropdown">
                                    <span class="flex items-center w-full">
                                        <img v-if="subgroup.icon" :src="asset('public/kpigroups/' + subgroup.icon)"
                                            alt="icon"
                                            style="height: 24px; width: 24px; margin-right: 8px; object-fit: contain;" />
                                        <span class="truncate">
                                            {{ subgroup.icon ? `Icono ${images.indexOf(subgroup.icon) + 1}` :
                                            'Selecciona un icono' }}
                                        </span>
                                    </span>
                                </button>
                                <teleport to="body">
                                    <div v-if="iconDropdownOpen" ref="dropdown"
                                        class="dropdown-fixed bg-white border rounded shadow-lg max-h-60 overflow-auto z-50"
                                        :style="dropdownStyle">
                                        <div v-for="(icon, index) in images" :key="icon" @click="selectIcon(icon)"
                                            class="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center">
                                            <img :src="asset('public/kpigroups/' + icon)" alt="icon"
                                                style="height: 24px; width: 24px; margin-right: 8px; object-fit: contain;" />
                                            <span>Icono {{ index + 1 }}</span>
                                        </div>
                                    </div>
                                </teleport>
                            </div>
                        </b-field>
                    </section>
                </div>
                <footer class="modal-card-foot_ border-grey-light border-t border-dashed mb-2">
                    <div class="w-full inline-flex justify-end mt-4">
                        <b-button type="is-primary" @click="saveSubgroup" :disabled="!subgroup.name">
                            {{ $t('add_generic') }}
                        </b-button>
                        <b-button @click="subgroupModalActive = false" class="ml-2">{{ $t('cancel') }}</b-button>
                    </div>
                </footer>
            </div>
        </b-modal>
        <!-- Asignar Actividades  -->
        <link-activities-modal :active="activitiesModalActive" :activities="activitiesArray"
            :assigned-activities="selectedActivities" :loading-activity="loadingActivity" @save="linkActivitiesToGroup"
            @close="activitiesModalActive = false" />
    </div>
</template>

<script>
import axios from 'axios'
import LinkActivitiesModal from './link-activities-modal.vue'
export default {
    props: {
        projectId: {
            type: Number,
            required: true
        },
        activityId: {
            type: Number,
            default: null
        }
    },
    components: {
        LinkActivitiesModal,
    },
    data() {
        return {
            kpiGroups: [],
            loading: false,
            subgroupModalActive: false,
            groupModalActive: false,
            selectedGroup: null,
            group: { name: '', description: '' },
            subgroup: { name: '', description: '', icon: '' },
            canCancel: ['escape', 'x', 'outside'],
            editingGroupId: null,
            activitiesModalActive: false,
            activitiesArray: [],
            selectedActivities: [],
            parents: [],
            loadingActivity: false,
            selectedGroupForActivities: null,
            images: [
                'image1.png', 'image2.png', 'image3.png', 'image4.png', 'image5.png',
                'image6.png', 'image7.png', 'image8.png', 'image9.png', 'image10.png'
            ],
            isLoading: false,
            iconDropdownOpen: false,
            dropdownStyle: {
                top: '0px',
                left: '0px',
                position: 'fixed'
            }
        }
    },
    mounted() {
        this.fetchKpiGroups();
        this.getProjectKpiActivities();
    },
    computed: {
        icons() {
            return this.images.map((img, idx) => ({
                value: img,
                label: `Icono ${idx + 1}`
            }));
        }
    },
    methods: {

        async fetchKpiGroups() {
            this.loading = true
            try {
                const params = { type: 1 };
                if (this.activityId) {
                    params.activity_id = this.activityId;
                }
                const { data } = await axios.get(`/api/v2/project/${this.projectId}/kpi-groups`, { params });
                this.kpiGroups = data.data
            } catch (e) {
                this.$buefy.toast.open({ message: this.$t('error_loading_data'), type: 'is-danger' })
            } finally {
                this.loading = false
            }
        },

        openSubgroupModal(group) {
            this.selectedGroup = group
            this.subgroupModalActive = true
        },

        editGroup(group) {
            this.group = { name: group.name, description: group.description };
            this.editingGroupId = group.id;
            this.groupModalActive = true;
        },

        async saveGroup() {
            try {
                const payload = {
                    name: this.group.name,
                    description: this.group.description,
                    type: 1,
                    parent_id: null
                };
                if (this.activityId) {
                    payload.activity_id = this.activityId;
                }

                if (this.editingGroupId) {
                    await axios.put(
                        `/api/v2/project/${this.projectId}/kpi-group/${this.editingGroupId}`,
                        payload
                    );
                } else {
                    await axios.post(`/api/v2/project/${this.projectId}/kpi-group`, payload);
                }
                this.groupModalActive = false;
                this.group = { name: '', description: '' };
                this.editingGroupId = null;
                this.fetchKpiGroups();
            } catch (e) {
                this.$buefy.toast.open({ message: this.$t('error_saving'), type: 'is-danger' });
            }
        },

        async saveSubgroup() {
            try {
                if (this.editingSubgroupId) {
                    await axios.put(
                        `/api/v2/project/${this.projectId}/kpi-group/${this.editingSubgroupId}`,
                        {
                            name: this.subgroup.name,
                            description: this.subgroup.description,
                            icon: this.subgroup.icon,
                            type: 1,
                            parent_id: this.selectedGroup ? this.selectedGroup.id : null
                        }
                    );
                } else {
                    await axios.post(`/api/v2/project/${this.projectId}/kpi-group`, {
                        name: this.subgroup.name,
                        description: this.subgroup.description,
                        icon: this.subgroup.icon,
                        type: 1,
                        parent_id: this.selectedGroup ? this.selectedGroup.id : null
                    });
                }
                this.subgroupModalActive = false;
                this.subgroup = { name: '', description: '', icon: '' };
                this.editingSubgroupId = null;
                this.fetchKpiGroups();
            } catch (e) {
                this.$buefy.toast.open({ message: this.$t('error_saving'), type: 'is-danger' });
            }
        },

        closeGroupModal() {
            this.groupModalActive = false;
        },

        editSubgroup(subgroup, parentGroup) {
            this.selectedGroup = parentGroup;
            this.subgroup = {
                name: subgroup.name,
                description: subgroup.description,
                icon: subgroup.icon
            };
            this.editingSubgroupId = subgroup.id;
            this.subgroupModalActive = true;
        },

        async deleteGroup(subgroup) {
            try {
                this.$dialog.confirm({
                    title: this.$t('delete'),
                    message: this.$t('delete_confirm_text', [subgroup.name]),
                    confirmText: this.$t('delete'),
                    type: 'is-danger',
                    hasIcon: true,
                    onConfirm: async () => {
                        const url = `/api/v2/project/${this.projectId}/kpi-group/${subgroup.id}`;
                        await axios.delete(url);
                        this.$buefy.toast.open({
                            message: this.$t('delete_successful'),
                            type: 'is-success'
                        });
                        this.fetchKpiGroups();
                    },
                });
            } catch (error) {
                this.$buefy.toast.open({
                    message: this.$t('error_deleting'),
                    type: 'is-danger'
                });
            }
        },

        async getProjectKpiActivities() {
            this.loadingActivity = true;
            try {
                const params = {};
                if (this.activityId) {
                    params.activity_id = this.activityId;
                }
                const { data } = await axios.get(`/api/v2/project/${this.projectId}/kpi-activities`, { params });
                if (data && data.success) {
                    const activitiesWithResources = data.data || [];
                    const allParents = data.parents || [];
                    const allActivities = [
                        ...activitiesWithResources,
                        ...allParents.filter(parent => !activitiesWithResources.some(a => a.id === parent.id))
                    ];
                    this.allActivities = allActivities;
                } else {
                    this.$buefy.toast.open({ message: this.$t('error_loading_activity_data'), type: 'is-danger' });
                }
            } catch (e) {
                this.$buefy.toast.open({ message: this.$t('error_loading_activity_data'), type: 'is-danger' });
            } finally {
                this.loadingActivity = false;
            }
        },

        openActivitiesModal(subgroup) {
            this.selectedGroupForActivities = subgroup;
            this.activitiesArray = this.allActivities || [];
            this.parents = this.activitiesArray
                .map(a => ({ name: a.parent_name, id: a.parent_id }))
                .filter((v, i, a) => v.id && a.findIndex(t => t.id === v.id) === i);
            this.selectedActivities = (subgroup.assignment_objects || [])
                .map(r => `res-${r.assignment_id}`);
            this.activitiesModalActive = true;
        },

        async linkActivitiesToGroup(selectedActivities) {
            if (!this.selectedGroupForActivities) return;
            const resourceIds = selectedActivities
                .filter(id => typeof id === 'string' && id.startsWith('res-'))
                .map(id => parseInt(id.replace(/^res-/, ''), 10));
            try {
                await axios.put(
                    `/api/v2/kpi-group/${this.selectedGroupForActivities.id}/assignments`,
                    { assignments: resourceIds }
                );
                this.$buefy.toast.open({ message: this.$t('saved_successfully'), type: 'is-success' });
                this.activitiesModalActive = false;
                this.fetchKpiGroups();
            } catch (e) {
                this.$buefy.toast.open({ message: this.$t('error_saving'), type: 'is-danger' });
            }
        },


        closeActivitiesModal() {
            this.activitiesModalActive = false;
        },

        exportKpiDaily() {
            this.isLoading = true;
            const groupIds = this.kpiGroups.map(g => g.id);

            axios.get(`/api/v2/project/${this.projectId}/kpi-report-daily`, {
                params: { groupIds }
            })
                .then(response => {
                    if (response.data && response.data.success && response.data.data) {
                        window.location.href = response.data.data;
                    } else {
                        this.$buefy.toast.open({ message: this.$t('error_exporting'), type: 'is-danger' });
                    }
                })
                .catch(() => {
                    this.$buefy.toast.open({ message: this.$t('error_exporting'), type: 'is-danger' });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        async confirmDeleteAssignment(subgroup, assignment) {
            this.$dialog.confirm({
                title: this.$t('delete'),
                message: this.$t('delete_confirm_text', [assignment.name]),
                confirmText: this.$t('delete'),
                type: 'is-danger',
                hasIcon: true,
                onConfirm: async () => {
                    try {
                        const remaining = subgroup.assignment_objects.filter(a => a.id !== assignment.id);
                        const assignmentIds = remaining.map(a => a.id);
                        await axios.put(
                            `/api/v2/kpi-group/${subgroup.id}/assignments`,
                            { assignments: assignmentIds }
                        );
                        subgroup.assignment_objects = remaining;
                        this.$buefy.toast.open({
                            message: this.$t('delete_successful'),
                            type: 'is-success'
                        });
                    } catch (e) {
                        this.$buefy.toast.open({
                            message: this.$t('error_deleting'),
                            type: 'is-danger'
                        });
                    }
                }
            });
        },

        toggleDropdown(event) {
            this.iconDropdownOpen = !this.iconDropdownOpen;
            this.$nextTick(() => {
                const rect = event.target.getBoundingClientRect();
                this.dropdownStyle.top = `${rect.bottom + window.scrollY}px`;
                this.dropdownStyle.left = `${rect.left + window.scrollX}px`;
                this.dropdownStyle.width = `${rect.width}px`;
            });
        },

        selectIcon(icon) {
            this.subgroup.icon = icon;
            this.iconDropdownOpen = false;
        },
       onViewKpi() {
            const groupIds = this.kpiGroups.map(g => g.id);
            this.$emit('ActivityHomeShowFullWidth', {
                type: 'daily',
                groupIds,
                projectId: this.projectId,
                activityId: this.activityId,
            });
        }
    }
}
</script>

<style lang="scss" scoped>
::v-deep .table td,
::v-deep .table th {
    vertical-align: middle !important;
}
</style>
